import { useState, useEffect, useCallback } from 'react';

// Compte optionnel. Le mode anonyme (useUser + motrusse_uid) reste le défaut ;
// un compte ne fait que RÉSOUDRE vers un UUID. Session = jeton opaque en
// localStorage (aucun cookie), envoyé en en-tête Authorization.
const TOKEN_KEY = 'motrusse_token';
const UID_KEY = 'motrusse_uid';

const authHeaders = (t) => ({ Authorization: `Bearer ${t}` });
const jsonHeaders = (t) => ({ 'Content-Type': 'application/json', ...(t ? authHeaders(t) : {}) });

function readToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

async function postJSON(url, body, token) {
  const res = await fetch(url, { method: 'POST', headers: jsonHeaders(token), body: JSON.stringify(body) });
  let data = {};
  try { data = await res.json(); } catch {}
  return { res, data };
}

const useAuth = () => {
  const [account, setAccount] = useState(null); // { handle } | null
  const [ready, setReady] = useState(false);

  // Au montage : si un jeton existe, on confirme la session (et on récupère le handle).
  useEffect(() => {
    const t = readToken();
    if (!t) { setReady(true); return; }
    fetch('/api/account/me', { headers: authHeaders(t) })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        // `owner` vient du SERVEUR : il commande l'affichage du raccourci vers
        // l'espace personnel. Rien à en déduire côté navigateur.
        if (d && d.handle) setAccount({ handle: d.handle, owner: !!d.owner });
        else { try { localStorage.removeItem(TOKEN_KEY); } catch {} }
      })
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);

  // Écrit jeton + UUID puis recharge : toute la progression/les stats se
  // reconstruisent proprement au montage (même mécanique que l'ancien applyId).
  const adopt = useCallback((token, uid) => {
    try {
      if (token) localStorage.setItem(TOKEN_KEY, token);
      if (uid) localStorage.setItem(UID_KEY, uid);
      window.location.reload();
    } catch {}
  }, []);

  // Inscription : rattache l'UUID anonyme courant (currentUid) au compte → rien
  // n'est perdu. Renvoie { ok, error } ou { ok, token, uid }.
  const register = useCallback(async (handle, passphrase, currentUid) => {
    const { res, data } = await postJSON('/api/account/register',
      { handle, passphrase, user_id: currentUid });
    if (!res.ok) return { ok: false, error: data.error || 'generic' };
    return { ok: true, token: data.token, uid: data.user_id };
  }, []);

  const login = useCallback(async (handle, passphrase) => {
    const { res, data } = await postJSON('/api/account/login', { handle, passphrase });
    if (!res.ok) return { ok: false, error: data.error || 'generic' };
    return { ok: true, token: data.token, uid: data.user_id };
  }, []);

  // Fusion cross-domaine : replie la progression de l'UUID local dans le compte.
  const mergeInto = useCallback(async (token, sourceUid) => {
    try { await postJSON('/api/account/merge', { source_uuid: sourceUid }, token); } catch {}
  }, []);

  const logout = useCallback(async () => {
    const t = readToken();
    if (t) { try { await fetch('/api/account/logout', { method: 'POST', headers: authHeaders(t) }); } catch {} }
    try { localStorage.removeItem(TOKEN_KEY); } catch {}
    window.location.reload();
  }, []);

  const deleteAccount = useCallback(async () => {
    const t = readToken();
    if (t) { try { await fetch('/api/account', { method: 'DELETE', headers: authHeaders(t) }); } catch {} }
    try { localStorage.removeItem(TOKEN_KEY); } catch {}
    window.location.reload();
  }, []);

  return { account, ready, register, login, mergeInto, adopt, logout, deleteAccount };
};

export default useAuth;
